import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, XCircle, Loader2, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import SEO from "@/components/SEO";
import { track, EVENT } from "@/lib/analytics";
import ResendVerificationInline from "@/components/ResendVerificationInline";
import SocialLoginButtons from "@/components/SocialLoginButtons";
import {
  FutureNetworkSignupAboveForm,
  FutureNetworkSignupBelowForm,
} from "@/components/FutureCollectorNetwork";

const USERNAME_RE = /^[A-Za-z0-9_-]{3,32}$/;
const CHECK_DEBOUNCE_MS = 400;

export default function Register() {
  const navigate = useNavigate();
  const location = useLocation();
  const rawNext = new URLSearchParams(location.search).get("next");
  // Iter V2 — preserve `?claim={cert_number}` through registration so the
  // authenticated collector lands on the claim form instead of the generic
  // dashboard. Both `next` and `claim` are honoured; `claim` wins.
  const claimParam = new URLSearchParams(location.search).get("claim");
  const safeClaim = (typeof claimParam === "string" && /^[A-Z]{2}-\d{4}-\d{4}$/.test(claimParam)) ? claimParam : null;
  const nextParam = safeClaim
    ? `/dashboard/claim?cert=${encodeURIComponent(safeClaim)}`
    : ((typeof rawNext === "string"
    && rawNext.startsWith("/") && !rawNext.startsWith("//")
    && !/^\/*[a-zA-Z][a-zA-Z0-9+.\-]*:/.test(rawNext)) ? rawNext : "/dashboard");

  // Phase C1 — sniff a pending cert number out of either the explicit
  // `?claim=` query param OR a `next=/claim?cert=…` continuation. This
  // is the value we send to the backend as `pending_claim_cert_number`
  // so the certificate context survives the email-verify round-trip.
  // Only the printed cert shape is accepted (`XX-YYYY-NNNN`).
  const pendingClaimCertNumber = (() => {
    if (safeClaim) return safeClaim;
    if (typeof rawNext !== "string") return null;
    const m = rawNext.match(/[?&]cert=([A-Za-z]{2}-\d{4}-\d{4})(?:$|&)/);
    return m ? m[1].toUpperCase() : null;
  })();

  const emailFormRef = useRef(null);
  const scrollToEmailForm = () => {
    const node = emailFormRef.current;
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
    // Focus the first field for keyboard-only users
    const el = node.querySelector("input[type='email']");
    if (el) setTimeout(() => el.focus(), 350);
  };

  const [form, setForm] = useState({
    email: "", password: "", password_confirm: "", display_name: "", username: "",
    accept_terms: false, marketing_product: false, marketing_event: false, marketing_newsletter: false,
    marketing_consent_email: false,
  });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [showPass, setShowPass] = useState(false);
  const [uname, setUname] = useState({ state: "idle", message: "" });
  const debounceRef = useRef(null);

  const on = (k) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: val }));
    if (errors[k]) setErrors((prev) => ({ ...prev, [k]: undefined }));
  };

  // -------- Live username availability check (debounced) --------

  React.useEffect(() => {
    track(EVENT.SIGNUP_STARTED, { login_method: "password" },
          { once: true, key: "signup-start" });
  }, []);

  useEffect(() => {
    const v = form.username.trim();
    if (!v) { setUname({ state: "idle", message: "" }); return; }
    if (!USERNAME_RE.test(v)) {
      setUname({ state: "invalid", message: "3–32 letters, numbers, _ or -" });
      return;
    }
    setUname({ state: "checking", message: "Checking…" });
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const r = await api.get(`/collector-auth/username-check?u=${encodeURIComponent(v)}`);
        if (r.data?.available) {
          setUname({ state: "available", message: r.data.message || "Available." });
        } else {
          setUname({ state: "taken", message: r.data.message || "Not available." });
        }
      } catch (_) {
        setUname({ state: "idle", message: "" });
      }
    }, CHECK_DEBOUNCE_MS);
    return () => debounceRef.current && clearTimeout(debounceRef.current);
  }, [form.username]);

  const passwordStrength = getStrength(form.password);

  const validate = () => {
    const err = {};
    if (!form.email.trim()) err.email = "Email is required.";
    else if (!/^\S+@\S+\.\S+$/.test(form.email.trim())) err.email = "Please enter a valid email address.";
    if (!form.password) err.password = "Password is required.";
    else if (form.password.length < 10) err.password = "Password must be at least 10 characters.";
    else if (!/[A-Za-z]/.test(form.password) || !/[0-9]/.test(form.password))
      err.password = "Password must contain letters and numbers.";
    if (!form.password_confirm) err.password_confirm = "Please confirm your password.";
    else if (form.password_confirm !== form.password) err.password_confirm = "Passwords don't match.";
    if (!form.display_name.trim()) err.display_name = "Display name is required.";
    if (!form.username.trim()) err.username = "Username is required.";
    else if (!USERNAME_RE.test(form.username.trim()))
      err.username = "Use 3–32 letters, numbers, underscore, or dash.";
    else if (uname.state === "taken") err.username = uname.message;
    if (!form.accept_terms) err.accept_terms = "You must accept the Terms and Privacy Policy.";
    return err;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    setErrors(err);
    if (Object.keys(err).length) return;
    setBusy(true);
    try {
      const { password_confirm: _pc, ...payload } = form;
      // Phase C1 — persist the pending cert number server-side so the
      // certificate-claim context survives the email verification hop.
      if (pendingClaimCertNumber) {
        payload.pending_claim_cert_number = pendingClaimCertNumber;
      }
      await api.post("/collector-auth/register", payload);
      track(EVENT.SIGNUP_COMPLETED, { login_method: "password" },
            { once: true, key: "signup" });
      setSubmitted(true);
      toast.success("Check your inbox for a verification link.");
    } catch (err2) {
      const detail = err2.response?.data?.detail;
      const msg = formatApiError(err2.response?.data);
      const lower = typeof detail === "string" ? detail.toLowerCase() : "";
      if (lower.includes("email")) setErrors((p) => ({ ...p, email: msg }));
      else if (lower.includes("username")) setErrors((p) => ({ ...p, username: msg }));
      else if (lower.includes("password")) setErrors((p) => ({ ...p, password: msg }));
      toast.error(msg);
    } finally { setBusy(false); }
  };

  if (submitted) return <SubmittedView email={form.email} />;

  return (
    <>
      <SEO title="Create your account · Collector Registry" />

      {/* ─── Iter V2 Phase B — claim context banner ────────────────────
          Displayed above the form when the visitor arrived via a
          certificate CTA (e.g. /register?claim=SC-2026-0001). Uses the
          dark/gold aesthetic to preserve the "award ceremony" feel from
          the certificate page. Does NOT alter the form itself. */}
      {safeClaim && <ClaimContextBanner cert={safeClaim} />}

      {/* ─── Signup Redesign · Google above the fold ────────────────────
          The Google/Social sign-up buttons must appear BEFORE any hero
          section so mobile visitors see them without scrolling. The
          full email/password form still lives below. */}
      <SocialTopStrip nextParam={nextParam} onEmailPress={scrollToEmailForm} />

      {/* ─── 1–6. Above the form ───────────────────────────────────────── */}
      <div className="container-gm pt-8 md:pt-10 pb-6 md:pb-8" data-testid="register-above-form">
        <FutureNetworkSignupAboveForm />
      </div>

      {/* ─── 7. Registration form ──────────────────────────────────────── */}
      <div
        ref={emailFormRef}
        id="register-email-form"
        className="bg-[var(--panini-yellow-soft)] border-y-2 border-[var(--ink)]"
        data-testid="register-page"
      >
        <div className="container-gm py-10 md:py-14">
          <div className="max-w-xl mx-auto">
            <Link
              to="/"
              className="inline-block kicker kicker-red mb-4"
              data-testid="register-brand"
            >
              Gil Maakaroun · Collector Registry
            </Link>
            <h1 className="h-display text-4xl md:text-5xl leading-none">
              Create your account.
            </h1>
            <p className="mt-3 text-[var(--smoke)] text-sm md:text-base">
              Free forever. Claim your permanent Public Collector ID and start
              registering certificates in under two minutes.
            </p>

            <div className="mt-8 rounded-3xl border-2 border-[var(--ink)] bg-white p-6 md:p-8 shadow-[8px_8px_0_var(--ink)] space-y-6">
              <SocialLoginButtons mode="register" returnTo={nextParam} />

              <form onSubmit={submit} className="space-y-4" data-testid="register-form" noValidate>
                <Field label="Email address" testid="reg-email"
                       type="email" autoComplete="email"
                       value={form.email} onChange={on("email")}
                       placeholder="you@example.com" error={errors.email} />

                <PasswordField
                  label="Password"
                  value={form.password}
                  onChange={on("password")}
                  error={errors.password}
                  show={showPass}
                  onToggle={() => setShowPass((v) => !v)}
                  testid="reg-password"
                  strength={passwordStrength}
                />

                <PasswordField
                  label="Confirm password"
                  value={form.password_confirm}
                  onChange={on("password_confirm")}
                  error={errors.password_confirm}
                  show={showPass}
                  onToggle={() => setShowPass((v) => !v)}
                  testid="reg-password-confirm"
                />

                <Field label="Display name" placeholder="How you want to appear publicly"
                       testid="reg-display" value={form.display_name}
                       onChange={on("display_name")} error={errors.display_name} />

                <UsernameField
                  value={form.username}
                  onChange={on("username")}
                  error={errors.username}
                  availability={uname}
                  testid="reg-username"
                />

                <label
                  className={`flex items-start gap-3 text-sm rounded-xl border-2 p-3 ${
                    errors.accept_terms ? "border-[var(--panini-red)]" : "border-[var(--line)]"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.accept_terms}
                    onChange={on("accept_terms")}
                    data-testid="reg-terms"
                    className="mt-0.5"
                  />
                  <span className="text-[var(--ink)]">
                    I&apos;ve read and accept the{" "}
                    <Link to="/terms-of-service" className="underline font-bold">Terms</Link>{" "}
                    and{" "}
                    <Link to="/privacy-policy" className="underline font-bold">Privacy Policy</Link>.
                  </span>
                </label>
                {errors.accept_terms && (
                  <div className="text-xs text-[var(--panini-red)] font-bold" data-testid="reg-terms-error">
                    {errors.accept_terms}
                  </div>
                )}

                {/* E8C · Canonical engagement-email opt-in shown
                    above the collapsed legacy fine-grained boxes. */}
                <div className="rounded-xl border-2 border-[var(--ink)] bg-white p-3">
                  <Check
                    label="Email me helpful CollectorUp updates about my albums, missing stickers, duplicates and collecting progress."
                    checked={form.marketing_consent_email}
                    onChange={on("marketing_consent_email")}
                    testid="reg-mkt-consent"
                  />
                  <div className="mt-1 text-[10px] text-[var(--smoke)] italic ml-6">
                    Optional. You can change this any time from your profile.
                  </div>
                </div>

                <details className="rounded-xl border border-[var(--line)] p-3 text-sm">
                  <summary className="cursor-pointer text-[11px] font-bold tracking-widest text-[var(--smoke)] uppercase">
                    Optional emails (you can change these anytime)
                  </summary>
                  <div className="mt-3 space-y-2">
                    <Check label="Product announcements" checked={form.marketing_product} onChange={on("marketing_product")} testid="reg-mkt-product" />
                    <Check label="Event announcements" checked={form.marketing_event} onChange={on("marketing_event")} testid="reg-mkt-event" />
                    <Check label="Newsletter" checked={form.marketing_newsletter} onChange={on("marketing_newsletter")} testid="reg-mkt-news" />
                  </div>
                </details>

                <button
                  type="submit"
                  disabled={busy}
                  data-testid="reg-submit"
                  className="btn-red w-full justify-center text-sm disabled:opacity-60"
                >
                  {busy ? "Creating account…" : "Create account"}
                </button>
              </form>

              <p className="pt-6 border-t border-[var(--line)] text-sm text-[var(--smoke)]">
                Already have an account?{" "}
                <Link
                  to="/login"
                  className="font-bold text-[var(--ink)] hover:text-[var(--panini-red)]"
                  data-testid="reg-goto-login"
                >
                  Sign in →
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ─── 8. Three reassurance cards ────────────────────────────────── */}
      <div className="container-gm pt-12 md:pt-16" data-testid="register-reassurance-wrap">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5" data-testid="register-reassurance">
          {[
            { title: "Free Collector Account", body: "No fees to create your identity. Certificates and profile are always free.", accent: "yellow" },
            { title: "Claim Verified Certificates", body: "Attach every album completion certificate to your permanent collector profile.", accent: "red" },
            { title: "Future Features Automatically Added", body: "As new Collector Network tools launch, your account unlocks them automatically.", accent: "green" },
          ].map((c, i) => (
            <div
              key={c.title}
              className={
                "rounded-2xl border-2 border-[var(--ink)] bg-white p-6 shadow-[6px_6px_0_var(--ink)] " +
                (c.accent === "yellow" ? "border-t-8 border-t-[var(--panini-yellow)]" :
                 c.accent === "red"    ? "border-t-8 border-t-[var(--panini-red)]" :
                                         "border-t-8 border-t-[var(--field-green)]")
              }
              data-testid={`register-reassurance-${i + 1}`}
            >
              <h4 className="font-display text-lg leading-tight">{c.title}</h4>
              <p className="text-[var(--smoke)] text-sm mt-2 leading-relaxed">{c.body}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ─── 9–12. Expandable details, trade preview, badges, nearby ───── */}
      <div className="container-gm py-12 md:py-16" data-testid="register-below-form">
        <FutureNetworkSignupBelowForm />
      </div>
    </>
  );
}

function SubmittedView({ email }) {
  return (
    <div className="container-gm py-20 max-w-lg" data-testid="register-success-page">
      <SEO title="Verify your email" />
      <div className="rounded-3xl border-2 border-[var(--ink)] bg-white p-8 shadow-[10px_10px_0_var(--panini-red)]">
        <div className="w-14 h-14 rounded-full bg-[var(--panini-yellow)] border-2 border-[var(--ink)] grid place-items-center">
          <CheckCircle2 size={26} className="text-[var(--ink)]" />
        </div>
        <div className="kicker kicker-yellow mt-6">Almost done</div>
        <h1 className="h-display text-3xl mt-2">Check your inbox.</h1>
        <p className="mt-4 text-[var(--smoke)]">
          We&apos;ve sent a verification link to{" "}
          <span className="font-bold text-[var(--ink)]">{email}</span>. Click it
          within 48 hours to activate your account and receive your permanent
          Public Collector ID.
        </p>
        <div className="mt-6 flex flex-col gap-3">
          <Link
            to="/login"
            className="btn-red text-sm w-full justify-center"
            data-testid="register-goto-login"
          >
            Go to sign in
          </Link>
          <ResendVerificationInline email={email} testidPrefix="register" />
        </div>
      </div>
    </div>
  );
}

// -------- Sub-components --------
function Field({ label, testid, error, ...rest }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">
        {label}
      </div>
      <input
        {...rest}
        data-testid={testid}
        aria-invalid={Boolean(error)}
        className={`gm-field ${error ? "border-[var(--panini-red)]" : ""}`}
      />
      {error && (
        <div className="text-xs text-[var(--panini-red)] font-bold mt-1" data-testid={`${testid}-error`}>
          {error}
        </div>
      )}
    </label>
  );
}

function PasswordField({ label, value, onChange, error, show, onToggle, testid, strength }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">
        {label}
      </div>
      <div className="relative">
        <input
          type={show ? "text" : "password"}
          value={value}
          onChange={onChange}
          data-testid={testid}
          autoComplete="new-password"
          className={`gm-field pr-11 ${error ? "border-[var(--panini-red)]" : ""}`}
          placeholder="At least 10 characters"
        />
        <button
          type="button"
          onClick={onToggle}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] hover:text-[var(--ink)]"
          aria-label={show ? "Hide password" : "Show password"}
          data-testid={`${testid}-toggle`}
        >
          {show ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {strength && value.length > 0 && (
        <div className="mt-2 flex items-center gap-2" data-testid={`${testid}-strength`}>
          <div className="flex-1 h-1.5 rounded-full bg-[var(--line)] overflow-hidden">
            <div
              className={`h-full transition-all ${strength.color}`}
              style={{ width: `${strength.pct}%` }}
            />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-widest text-[var(--smoke)]">
            {strength.label}
          </span>
        </div>
      )}
      {error && (
        <div className="text-xs text-[var(--panini-red)] font-bold mt-1" data-testid={`${testid}-error`}>
          {error}
        </div>
      )}
    </label>
  );
}

function UsernameField({ value, onChange, error, availability, testid }) {
  const stateMeta = {
    idle: { color: "", icon: null },
    checking: { color: "text-[var(--smoke)]", icon: <Loader2 size={14} className="animate-spin" /> },
    invalid: { color: "text-[var(--panini-red)]", icon: <XCircle size={14} /> },
    taken: { color: "text-[var(--panini-red)]", icon: <XCircle size={14} /> },
    available: { color: "text-[color:var(--field-green)]", icon: <CheckCircle2 size={14} /> },
  }[availability?.state || "idle"];
  const displayError = error && availability?.state !== "checking" ? error : null;
  return (
    <label className="block">
      <div className="text-[11px] font-bold tracking-widest text-[var(--ink)] uppercase mb-1.5">
        Username
      </div>
      <div className="relative">
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--smoke)] font-mono text-sm">@</span>
        <input
          value={value}
          onChange={onChange}
          data-testid={testid}
          aria-invalid={Boolean(displayError)}
          className={`gm-field pl-8 pr-11 ${displayError ? "border-[var(--panini-red)]" : ""}`}
          placeholder="collector_handle"
          maxLength={32}
        />
        {stateMeta.icon && (
          <span
            className={`absolute right-3 top-1/2 -translate-y-1/2 ${stateMeta.color}`}
            data-testid={`${testid}-status`}
          >
            {stateMeta.icon}
          </span>
        )}
      </div>
      <div className="mt-1 flex justify-between items-center text-[11px]">
        <span className={stateMeta.color} data-testid={`${testid}-message`}>
          {displayError || availability?.message || "3–32 letters, numbers, underscore, or dash."}
        </span>
        <span className="text-[var(--smoke)]">{value.length}/32</span>
      </div>
    </label>
  );
}

function Check({ label, checked, onChange, testid }) {
  return (
    <label className="flex items-center gap-3 text-sm text-[var(--ink)]">
      <input type="checkbox" checked={checked} onChange={onChange} data-testid={testid} className="w-4 h-4" />
      <span>{label}</span>
    </label>
  );
}

/**
 * Iter V2 Phase B — full-width dark hero shown above the registration
 * form when the visitor arrived via a certificate claim link. Reinforces
 * the "you are about to be verified" moment and previews the exact
 * certificate they're about to attach — without exposing anything
 * private (the certificate number is the ONLY value shown, and it is
 * already public).
 */
function ClaimContextBanner({ cert }) {
  return (
    <div
      className="relative w-full bg-[var(--ink)] text-white overflow-hidden border-b-2 border-[var(--collector-gold)]/40"
      data-testid="register-claim-banner"
    >
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <div
          className="absolute -top-24 left-1/2 -translate-x-1/2 w-[900px] h-[400px] rounded-full opacity-[0.28] blur-3xl"
          style={{ background: "radial-gradient(closest-side, rgba(200,162,77,0.65), transparent 70%)" }}
        />
        <div
          className="absolute inset-0 opacity-[0.05]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px)," +
              "linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
      </div>

      <div className="relative container-gm py-10 md:py-14 max-w-3xl">
        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-[var(--collector-gold)]/40 bg-[var(--collector-gold)]/10 text-[var(--collector-gold-soft)] text-[11px] font-bold tracking-widest uppercase">
          One step away
        </div>
        <h2
          className="mt-5 font-serif text-3xl md:text-5xl leading-tight"
          data-testid="register-claim-headline"
        >
          Create your account to claim
          <br className="hidden md:block" />{" "}
          <span className="text-[var(--collector-gold)]">certificate {cert}</span>.
        </h2>
        <p className="mt-4 max-w-xl text-white/70 text-sm md:text-base leading-relaxed">
          Free · Secure · Takes less than 60 seconds. We&apos;ll bring you
          straight back to complete the claim after sign-up.
        </p>
      </div>
    </div>
  );
}

function getStrength(pw) {
  if (!pw) return null;
  let score = 0;
  if (pw.length >= 10) score++;
  if (pw.length >= 14) score++;
  if (/[A-Z]/.test(pw) && /[a-z]/.test(pw)) score++;
  if (/[0-9]/.test(pw)) score++;
  if (/[^A-Za-z0-9]/.test(pw)) score++;
  const meta = [
    { pct: 20, label: "Weak", color: "bg-[var(--panini-red)]" },
    { pct: 35, label: "Fair", color: "bg-[var(--panini-red)]" },
    { pct: 55, label: "OK", color: "bg-[var(--panini-yellow-deep)]" },
    { pct: 75, label: "Good", color: "bg-[var(--panini-yellow-deep)]" },
    { pct: 90, label: "Strong", color: "bg-[color:var(--field-green)]" },
    { pct: 100, label: "Excellent", color: "bg-[color:var(--field-green)]" },
  ];
  return meta[Math.min(score, meta.length - 1)];
}

/**
 * Signup Redesign — Google/Social sign-up strip that sits above every
 * hero block on the Register page. It is the very first interactive
 * surface a visitor sees, which the redesign brief mandated ("Google
 * above the fold"). If no social provider is enabled the strip renders
 * nothing so the page height stays honest.
 *
 * Phase C1 — sits directly below the Google button, a same-size
 * "Sign Up with Email" button that scrolls the page to the email
 * registration form without leaving the certificate-claim context.
 */
function SocialTopStrip({ nextParam, onEmailPress }) {
  return (
    <div
      className="border-b-2 border-[var(--ink)] bg-white"
      data-testid="register-social-top"
    >
      <div className="container-gm py-6 md:py-8 max-w-2xl">
        <div className="text-center mb-4">
          <div className="kicker kicker-red mb-2">Fastest way in</div>
          <h2 className="font-serif text-2xl md:text-3xl leading-tight">
            Continue with your Google account.
          </h2>
          <p className="mt-2 text-sm text-[var(--smoke)]">
            No password to remember. We&apos;ll bring you back here to finish setup.
          </p>
        </div>
        <SocialLoginButtons mode="register" returnTo={nextParam} showDivider={false} />
        <button
          type="button"
          onClick={onEmailPress}
          data-testid="register-signup-with-email"
          className="btn-black w-full justify-center text-sm mt-3"
        >
          Sign up with email
        </button>
        <p className="mt-2 text-[11px] text-center text-[var(--smoke)]">
          Prefer a password? Use the email form just below.
        </p>
      </div>
    </div>
  );
}
